namespace $ {

	type Definition = $shm_hitalama_map_widget_entity_custom
	type Context = { definition: Definition, version: string, note: string, date: string, author: string, repository: string, commit: string }
	type Plan = Context & { modules: number }

	export class $shm_hitalama_components_versions extends $mol_object {

		definition() { return null as any as Definition }
		owner() { return this.$.$shm_hitalama_components_owner.definition( this.definition() ) }

		static number( version: string ) {
			if( !/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/.test( version ) ) throw new Error( 'Укажите версию в формате 0.0.1.' )
			const parts = version.split( '.' ).map( Number )
			if( parts.some( part => !Number.isSafeInteger( part ) ) ) throw new Error( 'Слишком большой номер версии.' )
			return parts
		}

		static compare( left: string, right: string ) {
			const a = this.number( left ), b = this.number( right )
			return a[0] - b[0] || a[1] - b[1] || a[2] - b[2]
		}

		store() {
			const store = this.owner().Versions_store()?.remote() ?? null
			if( store ) this.$.$shm_hitalama_map_component_ready.read( store )
			return store
		}

		archived_entries() {
			const definition = this.definition()
			if( this.owner().link().str === definition.link().str ) return []
			const store = definition.Versions_store()?.remote()
			if( !store ) return []
			this.$.$shm_hitalama_map_component_ready.read( store )
			return ( store.Entries()?.remote_list() ?? [] ).filter( entry => entry.info() ).sort( ( a, b ) =>
				$shm_hitalama_components_versions.compare( b.info()!.version, a.info()!.version ) )
		}

		entries() {
			return ( this.store()?.Entries()?.remote_list() ?? [] ).filter( entry => entry.info() ).sort( ( a, b ) => {
				const left = a.info()!, right = b.info()!
				return $shm_hitalama_components_versions.compare( right.version, left.version )
					|| right.date.localeCompare( left.date ) || b.link().str.localeCompare( a.link().str )
			})
		}

		current() { return this.entries()[0] ?? null }

		next() {
			const current = this.current()?.info()
			if( !current ) return '0.0.1'
			const parts = $shm_hitalama_components_versions.number( current.version )
			return [ parts[0], parts[1], parts[2] + 1 ].join( '.' )
		}

		label() {
			const info = this.current()?.info()
			return info ? `Текущая версия v${ info.version } (${ this.$.$shm_date.text( info.date.slice( 0, 10 ) ) })` : 'Текущая версия ещё не создана'
		}

		private contexts = new WeakMap< object, Context >()
		context( operation: object, version: string, note: string, reference?: { repository: string, commit: string } ) {
			const previous = this.contexts.get( operation )
			if( previous ) return previous
			$shm_hitalama_components_versions.number( version )
			if( !reference || !/^[a-f0-9]{40}$/.test( reference.commit ) ) throw new Error( 'Версия должна ссылаться на GitHub-коммит (40 символов SHA).' )
			const context = { definition: this.owner(), version, note: note.trim(), date: new Date().toISOString(),
				repository: this.$.$shm_hitalama_components_github.repository( reference.repository ), commit: reference.commit,
				author: this.$.$giper_baza_auth.current().pass().lord().str }
			this.contexts.set( operation, context )
			return context
		}

		plan( context: Context ): Plan {
			if( !context.definition.can_change() ) throw new Error( 'Нет прав на выпуск версии приложения.' )
			const files = JSON.stringify( this.$.$shm_hitalama_components_github.files( context.definition ) )
			$mol_wire_sync( this ).verify( context.repository, context.commit, files, this.$.$shm_hitalama_settings.github_token() )
			return { ...context, modules: JSON.parse( JSON.parse( files )['.hitalama.json'] ).modules.length }
		}
		@ $mol_mem
		github() { return new this.$.$shm_hitalama_components_github }
		async verify( repository: string, commit: string, files: string, token: string ) {
			const github = this.github()
			const revision = await github.request( repository, token, '/git/commits/' + commit )
			const tree = await github.request( repository, token, '/git/trees/' + revision.tree.sha + '?recursive=1' )
			if( tree.truncated ) throw new Error( 'GitHub вернул неполное дерево коммита.' )
			const remote = new Map( tree.tree.filter( ( item: any ) => item.type === 'blob' ).map( ( item: any ) => [ item.path, item.sha ] ) )
			const hashes = await github.hashes( files )
			if( Object.entries( hashes ).some( ([ path, hash ]) => remote.get( path ) !== hash ) ) throw new Error( 'Коммит не совпадает с текущим кодом приложения. Сначала сохраните актуальные исходники на GitHub.' )
			return true
		}

		private drafts = new WeakMap< Context, { entry: $shm_hitalama_components_versions_entry } >()
		draft( context: Context ) {
			let draft = this.drafts.get( context )
			if( !draft ) {
				draft = { entry: context.definition.land().area_make().Data( this.$.$shm_hitalama_components_versions_entry ) }
				this.drafts.set( context, draft )
			}
			return draft
		}

		ensure_store( definition: Definition ) {
			definition = this.$.$shm_hitalama_components_owner.definition( definition )
			const existing = definition.Versions_store()?.remote()
			if( existing ) return existing
			// All clients allocate the same area even when their first releases race.
			const store = definition.land().area_make( $mol_hash_string( definition.link().str + ':application-versions-v1' ) ).Data( this.$.$shm_hitalama_components_versions_store )
			const owner = store.Owner()?.val()
			if( owner && owner !== definition.link().str ) throw new Error( 'Конфликт хранилища версий.' )
			store.Owner( null )!.val( definition.link().str )
			definition.Versions_store( null )!.remote( store )
			return store
		}

		@ $mol_action
		publish( operation: object, version: string, note: string, reference?: { repository: string, commit: string } ) {
			if( !this.owner().can_change() ) throw new Error( 'Нет прав на выпуск версии приложения.' )
			const context = this.context( operation, version, note, reference )
			const completed = this.drafts.get( context )
			if( completed && this.store()?.Entries()?.has( completed.entry.link() ) ) return completed.entry
			const plan = $mol_wire_sync( this ).plan( context )
			const store = $mol_wire_sync( this ).ensure_store( context.definition )
			const previous = this.drafts.get( context )
			if( previous && store.Entries()?.has( previous.entry.link() ) ) return previous.entry
			for( const entry of store.Entries()?.remote_list() ?? [] ) {
				if( entry.info()?.version === context.version ) throw new Error( `Версия ${ context.version } уже существует.` )
			}
			const draft = $mol_wire_sync( this ).draft( context )
			draft.entry.Info( null )!.val({ version: context.version, date: context.date, note: context.note,
				author: context.author, root: context.definition.class_name(), modules: plan.modules, repository: context.repository, commit: context.commit })
			store.Entries( null )!.remote_add( draft.entry )
			return draft.entry
		}
	}
}
